import Logo from "@/Icons/v2/Logo";
import { FAQIcon } from "@/Icons/v2/SidebarIcons/FAQ";
import { SidebarToggleIcon } from "@/Icons/v2/SidebarIcons/SidebarToggle";
import { SupportIcon } from "@/Icons/v2/SidebarIcons/Support";
import { useConfig } from "@/api/hooks/useConfig";
import { useTrans } from "@/translations";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { twMerge } from "tailwind-merge";

import { NavHeaderTypeEnum } from "./Sidebar.interface";
import { NavSection } from "./components/NavSection";
import { StyledLinkButton } from "./components/StyledLinkButton";
import { Portal } from "./components/utils";

// Below this width the sidebar becomes an off-canvas drawer toggled by the
// header's hamburger button, instead of the manual icon-rail collapse.
const MOBILE_BREAKPOINT = 751;

type Props = {
  collapsedSidebar: boolean;
  setCollapsedSidebar: (value: boolean) => void;
  mobileNavOpen: boolean;
  onCloseMobileNav: () => void;
};

export const SidebarRE: React.FC<Props> = ({
  collapsedSidebar,
  setCollapsedSidebar,
  mobileNavOpen,
  onCloseMobileNav,
}) => {
  const { config } = useConfig();
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => window.innerWidth < MOBILE_BREAKPOINT,
  );
  const onCloseMobileNavRef = useRef(onCloseMobileNav);
  onCloseMobileNavRef.current = onCloseMobileNav;

  useEffect(() => {
    const checkWidth = () => {
      const isBelowBreakpoint = window.innerWidth < MOBILE_BREAKPOINT;

      setIsMobileViewport(isBelowBreakpoint);

      // Don't leave the drawer "open" once the layout has switched back to
      // the desktop icon-rail/expanded sidebar.
      if (!isBelowBreakpoint) {
        onCloseMobileNavRef.current();
      }
    };

    checkWidth();

    window.addEventListener("resize", checkWidth);
    return () => window.removeEventListener("resize", checkWidth);
  }, []);

  const toggleSidebar = useCallback(() => {
    setCollapsedSidebar(!collapsedSidebar);
  }, [collapsedSidebar, setCollapsedSidebar]);

  if (!config) return null;

  const faqKey = config.useExternalCRM ? "hints:faq_BIR" : "hints:faq";

  return (
    <>
      {mobileNavOpen && (
        <Portal>
          <div
            className="fixed inset-0 z-[55] bg-black/40 min-[751px]:hidden"
            onClick={onCloseMobileNav}
          />
        </Portal>
      )}
      {collapsedSidebar && !isMobileViewport ? (
        <CollapsedSidebarView faqKey={faqKey} toggleSidebar={toggleSidebar} />
      ) : (
        <ExpandedSidebarView
          faqKey={faqKey}
          toggleSidebar={toggleSidebar}
          mobileNavOpen={mobileNavOpen}
        />
      )}
    </>
  );
};

type ViewProps = {
  faqKey: string;
  toggleSidebar: () => void;
};

const CollapsedSidebarView: React.FC<ViewProps> = ({
  faqKey,
  toggleSidebar,
}) => {
  const { t } = useTrans();

  return (
    <aside
      className={twMerge(
        `bg-lime fixed inset-y-0 left-0 z-10 flex w-[55px] transform flex-col transition duration-200 ease-in-out md:translate-x-0`,
      )}
    >
      <div
        className={twMerge(
          "bg bg-buttery-lime z-20 flex h-16 w-full items-center justify-center",
        )}
      >
        <Link to="/">
          <div className="flex items-center">
            <Logo />
          </div>
        </Link>
      </div>
      <nav className="overflow-hidden py-[9.9px]">
        <div
          className="flex w-full cursor-pointer justify-center"
          onClick={toggleSidebar}
        >
          <SidebarToggleIcon />
        </div>
        <ul>
          <NavSection type={NavHeaderTypeEnum.Overview} collapsedSidebar />
          <NavSection type={NavHeaderTypeEnum.Customers} collapsedSidebar />
          <NavSection type={NavHeaderTypeEnum.Setup} collapsedSidebar />
        </ul>
      </nav>
      <div className="mt-auto py-[14px]">
        <div className="border-y border-y-1 border-y-[#1d1c1c1a] py-[10px]">
          <StyledLinkButton
            id="faq"
            link={t(faqKey)}
            tooltipText="sidebarLabels:faq"
            icon={<FAQIcon />}
            isCollapsed
          />
          <StyledLinkButton
            id="contact-support"
            link="mailto:support@carrot.tech"
            tooltipText="sidebarLabels:contactSupport"
            icon={<SupportIcon />}
            isCollapsed
          />
        </div>
        <div className="h-7" />
      </div>
    </aside>
  );
};

const ExpandedSidebarView: React.FC<ViewProps & { mobileNavOpen: boolean }> = ({
  faqKey,
  toggleSidebar,
  mobileNavOpen,
}) => {
  const { t } = useTrans();

  return (
    <aside
      className={twMerge(
        "bg-lime fixed inset-y-0 left-0 z-[60] flex w-[210px] flex-col transition-transform duration-200 ease-in-out min-[751px]:z-10 min-[751px]:translate-x-0",
        mobileNavOpen
          ? "translate-x-0 shadow-[0_14px_40px_rgba(0,0,0,0.28)]"
          : "-translate-x-full",
      )}
    >
      <div
        className={twMerge(
          "bg bg-buttery-lime z-10 z-20 flex h-16 min-h-16 w-full items-center justify-between px-[14.85px]",
        )}
      >
        <Link to="/">
          <div className="flex items-center">
            <Logo />

            <div className="font-signifier ml-1.5">
              <div className="text-[12px]">Carrot</div>
              <div className="text-[21px]/[21px]">Insight</div>
            </div>
          </div>
        </Link>
        <div
          className="cursor-pointer max-[750px]:hidden"
          onClick={toggleSidebar}
        >
          <SidebarToggleIcon />
        </div>
      </div>
      <nav className="overflow-auto py-6">
        <ul>
          <NavSection type={NavHeaderTypeEnum.Overview} />
          <NavSection type={NavHeaderTypeEnum.Customers} />
          <NavSection type={NavHeaderTypeEnum.Setup} />
        </ul>
      </nav>
      <div className="mt-auto py-[14px]">
        <div className="border-y border-y-1 border-y-[#1d1c1c1a] py-[10px]">
          <StyledLinkButton
            id="faq"
            link={t(faqKey)}
            tooltipText="sidebarLabels:faq"
            icon={<FAQIcon />}
          />
          <StyledLinkButton
            id="contact-support"
            link="mailto:support@carrot.tech"
            tooltipText="sidebarLabels:contactSupport"
            icon={<SupportIcon />}
          />
        </div>
        <p className="text-forest mt-[11px] ml-[25px] text-[11px]">
          Carrot Insight v{import.meta.env.VITE_VERSION}
        </p>
      </div>
    </aside>
  );
};

export default SidebarRE;
